import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useChat } from '../hooks/useChat';
import { Send, Loader, X, MessageSquare, Target, Sparkles, Lightbulb, RotateCcw } from 'lucide-react';

const InputContainer = styled.div`
  background: white;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 0 6px;
  cursor: ns-resize;
  user-select: none;
  touch-action: none;
`;

const HandlePill = styled.div`
  width: 36px;
  height: 4px;
  background: #CBD5E0;
  border-radius: 2px;
  transition: background 0.2s;
  ${DragHandle}:hover & { background: #1A8B8B; }
`;

const InputContent = styled.div`
  padding: 8px 40px 24px;
  overflow: hidden;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  direction: rtl;
`;

const InputField = styled.textarea`
  flex: 1;
  width: 100%;
  min-height: 44px;
  max-height: 80px;
  border: 2px solid #e2e8f0;
  border-radius: 20px;
  padding: 10px 18px;
  font-size: 16px;
  line-height: 1.5;
  font-family: 'Inter', 'Noto Sans Arabic', sans-serif;
  color: #2d3748;
  background: white;
  resize: none;
  outline: none;
  transition: all 0.2s ease;
  direction: rtl;
  text-align: right;

  &::placeholder {
    color: #a0aec0;
    direction: rtl;
    text-align: right;
  }

  &:focus {
    border-color: #1A8B8B;
    box-shadow: 0 0 0 3px rgba(26, 139, 139, 0.1);
  }

  &:disabled {
    background: #F0F7F7;
    cursor: not-allowed;
    opacity: 0.6;
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(26, 139, 139, 0.3);
    border-radius: 2px;
  }
`;

const SendButton = styled(motion.button)`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(26, 139, 139, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top: 2px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
`;

const InterruptButton = styled(motion.button)`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e53e3e 0%, #c53030 100%);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(229, 62, 62, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const SuggestedQueries = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
  width: 100%;
  direction: rtl;
  justify-content: flex-start;
`;

const SuggestionChip = styled(motion.button)`
  background: rgba(26, 139, 139, 0.1);
  border: 1px solid rgba(26, 139, 139, 0.2);
  color: #1A8B8B;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(26, 139, 139, 0.2);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CharacterCount = styled.div`
  position: absolute;
  bottom: 8px;
  left: 20px;
  font-size: 12px;
  color: #a0aec0;
  pointer-events: none;
  direction: ltr;
`;

const InputFieldContainer = styled.div`
  position: relative;
  flex: 1;
`;

// Mode Switcher Styled Components
const ModeSwitcherContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
  direction: rtl;
`;

const ModeButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid ${props => props.$active ? '#1A8B8B' : '#D0E0E0'};
  background: ${props => props.$active ? 'linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%)' : 'white'};
  color: ${props => props.$active ? 'white' : '#0D1B2A'};

  &:hover {
    border-color: #1A8B8B;
    transform: translateY(-1px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const GoalInputPanel = styled(motion.div)`
  background: linear-gradient(135deg, #F0F7F7 0%, #E8F0F0 100%);
  border: 1px solid #D0E0E0;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  direction: rtl;
`;

const GoalInputField = styled.textarea`
  width: 100%;
  min-height: 80px;
  border: 2px solid #D0E0E0;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 15px;
  resize: none;
  direction: rtl;
  text-align: right;
  font-family: 'Inter', 'Noto Sans Arabic', sans-serif;
  background: white;

  &:focus {
    border-color: #1A8B8B;
    outline: none;
    box-shadow: 0 0 0 3px rgba(26, 139, 139, 0.1);
  }

  &::placeholder {
    color: #a0aec0;
  }

  &:disabled {
    background: #F0F7F7;
    cursor: not-allowed;
  }
`;

const GoalSubmitButton = styled(motion.button)`
  background: linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  cursor: pointer;
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 8px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const GoalSuggestionsPanel = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
`;

const GoalSuggestionCard = styled(motion.button)`
  background: white;
  border: 1px solid #D0E0E0;
  border-radius: 10px;
  padding: 12px 16px;
  text-align: right;
  direction: rtl;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #1A8B8B;
    transform: translateX(-2px);
    box-shadow: 0 2px 8px rgba(26, 139, 139, 0.15);
  }
`;

const SuggestionLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SuggestionQuery = styled.div`
  font-size: 13px;
  color: #718096;
`;

const GoalExplanation = styled.div`
  background: white;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 12px;
  color: #1A8B8B;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ResetGoalButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 6px;
  background: white;
  border: 1px solid #D0E0E0;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  color: #1A8B8B;
  cursor: pointer;
  margin-top: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: #F0F7F7;
    border-color: #1A8B8B;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// Predefined Goals Grid
const PredefinedGoalsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const PredefinedGoalCard = styled(motion.button)`
  background: white;
  border: 1px solid #D0E0E0;
  border-radius: 10px;
  padding: 14px;
  text-align: right;
  direction: rtl;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;

  &:hover {
    border-color: #1A8B8B;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(26, 139, 139, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const GoalCardIcon = styled.span`
  font-size: 24px;
`;

const GoalCardTitle = styled.h4`
  font-size: 13px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
`;

const GoalCardDescription = styled.p`
  font-size: 11px;
  color: #718096;
  margin: 0;
  line-height: 1.3;
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin: 12px 0;
  direction: rtl;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e2e8f0;
  }

  span {
    padding: 0 12px;
    color: #a0aec0;
    font-size: 12px;
    font-weight: 500;
  }
`;

// Database-specific suggested queries
const SUGGESTED_QUERIES_BY_DB = {
  economy_sqlite: [
    "أظهر الإيرادات النفطية من 2019 إلى 2023",
    "قارن الإيرادات النفطية مع غير النفطية من 2020 إلى 2023",
    "أظهر نمو الناتج المحلي الحقيقي من 2018 إلى 2022"
  ],
  takamol_sqlite: [
    "ما هو متوسط عدد الموظفين لكل نشاط اقتصادي، مع عرض النشاطات التي لديها أكثر من ٥٠ موظف فقط؟",
    "أعرض توزيع الموظفين حسب الجنسية عبر السنوات، مع تسليط الضوء على أكثر ٥ جنسيات نموًا",
    "ما نسبة كل نشاط اقتصادي من إجمالي الموظفين في كل مدينة؟",
    "أظهر العلاقة بين عدد المنشآت وإجمالي عدد الموظفين لكل مدينة",
    "ما هو عدد الموظفين الذكور في المنشآت ذات النطاق الأخضر؟",
    "ما هو متوسط عمر الموظفين الذين تم نقلهم بين المنشآت؟",
    "ما هو متوسط الراتب للوظائف التي تتطلب مؤهل دكتوراه؟",
    "كم عدد العقود التي تم إلغاؤها من قبل الموظفين؟"
  ],
  takamol_postgres: [
    "اذكر المنشآت التي لديها عدد موظفين غير سعوديين أكثر من عدد تصاريح العمل؟",
    "ما هو عدد الموظفين الذكور في المنشآت ذات النطاق الأخضر؟",
    "كم عدد التأشيرات الصادرة لوظيفة مهندس؟",
    "ما هو متوسط عمر الموظفين الذين تم نقلهم بين المنشآت؟",
    "ما هو إجمالي عدد الموظفين غير السعوديين في المنشآت الصغيرة؟",
    "ما هو متوسط الراتب للوظائف التي تتطلب مؤهل دكتوراه؟",
    "ما هو عدد التأشيرات المصدرة لكل نوع من أنواع التأشيرات؟",
    "كم عدد العقود التي تم إلغاؤها من قبل الموظفين؟"
  ],
  koora_sqlite: [
    "من هم أفضل 10 هدافين في الدوري؟",
    "ما هي الفرق التي سجلت أكثر الأهداف؟", 
    "كم عدد البطاقات الحمراء لكل فريق مع ذكر اسم كل فريق؟",
    "ما هو متوسط أهداف المباريات؟",
    "أي الفرق فازت بأكثر المباريات على أرضها؟",
    "من هم اللاعبون الأجانب الأكثر تسجيلاً؟"
  ],
  koora_postgres: [
    "من هم أفضل 10 هدافين في الدوري؟",
    "ما هي الفرق التي سجلت أكثر الأهداف؟", 
    "كم عدد البطاقات الحمراء لكل فريق مع ذكر اسم كل فريق؟",
    "ما هو متوسط أهداف المباريات؟",
    "أي الفرق فازت بأكثر المباريات على أرضها؟",
    "من هم اللاعبون الأجانب الأكثر تسجيلاً؟"
  ],
  mof_sqlite: [
    "كم عدد الجهات الحكومية المشمولة بالميزانية العامة للدولة للعام الحالي؟",
    "ما هي أكثر جهة تنفيذا للمناقلات في التكاليف لكل عام من الأعوام السابقة؟",
    "كم تبلغ قيمة المناقلات التي تم تنفيذها خلال العام الحالي لبند استهلاك الكهرباء؟",
    "كم يبلغ عدد المناقلات التي تم تنفيذها خلال العام الحالي للجهة وزارة الرياضة؟",
    "كم تبلغ نسبة الزيادة/الخفض في الميزانية للتصنيف الاقتصادي 'المستلزمات المكتبية والكساوي واللوازم غير الطبية' عن السنوات 2021، 2022، و2023؟"
  ],
  mof_postgres: [
    "كم عدد الجهات الحكومية المشمولة بالميزانية العامة للدولة للعام الحالي؟",
    "ما هي أكثر جهة تنفيذا للمناقلات في التكاليف لكل عام من الأعوام السابقة؟",
    "كم تبلغ قيمة المناقلات التي تم تنفيذها خلال العام الحالي لبند استهلاك الكهرباء؟",
    "كم يبلغ عدد المناقلات التي تم تنفيذها خلال العام الحالي للجهة وزارة الرياضة؟",
    "كم تبلغ نسبة الزيادة/الخفض في الميزانية للتصنيف الاقتصادي 'المستلزمات المكتبية والكساوي واللوازم غير الطبية' عن السنوات 2021، 2022، و2023؟"
  ],
  kfc_postgres: [
    // Simple chart-generating questions from ground truth optimized
    "اعرض عدد المعاملات حسب نوع المركبة",
    "ما هي أكثر طريقة دفع استخداماً؟",
    "اعرض عدد المعاملات حسب الاتجاه"
  ]
};

// Predefined goals based on stakeholder needs
const PREDEFINED_GOALS_BY_DB = {
  kfc_postgres: [
    {
      icon: '📱',
      title: 'تحليل المدفوعات الرقمية',
      description: 'نسبة الدفع الرقمي مقابل النقدي',
      goal: 'أريد فهم نسبة استخدام المدفوعات الرقمية (RFID، المحفظة الإلكترونية، QR) مقارنة بالدفع النقدي والبطاقات، وتحديد فرص زيادة التبني الرقمي'
    },
    {
      icon: '🔄',
      title: 'المسافرين المتكررين',
      description: 'أنماط العبور المتكرر',
      goal: 'أريد تحليل المسافرين المتكررين عبر الجسر - من هم؟ كم مرة يعبرون؟ ما طرق الدفع المفضلة لديهم؟'
    },
    {
      icon: '📉',
      title: 'اتجاهات المرور',
      description: 'التغيرات في حجم المرور والإيرادات',
      goal: 'أريد فهم اتجاهات حركة المرور والإيرادات - هل هناك انخفاض؟ ما الأسباب المحتملة؟ مقارنة الفترات الزمنية'
    },
    {
      icon: '🛣️',
      title: 'تحليل الاتجاهات',
      description: 'مقارنة السعودية والبحرين',
      goal: 'أريد تحليل حركة المرور حسب الاتجاه - من السعودية للبحرين مقابل من البحرين للسعودية، وأوقات الذروة لكل اتجاه'
    },
    {
      icon: '🔍',
      title: 'تحديد المشكلات',
      description: 'اكتشاف نقاط الضعف',
      goal: 'أريد تحديد المشكلات التشغيلية - أين تكمن نقاط الضعف؟ ما المسارات الأقل كفاءة؟ كيف يمكن تحسين تجربة المسافر؟'
    }
  ]
};

const MAX_CHARS = 500;

function ChatInput() {
  const {
    currentInput,
    isProcessing,
    actions,
    dbId,
    chatMode,
    goalSuggestions,
    isLoadingGoalSuggestions,
    goalExplanation,
    messages
  } = useChat();

  // Get suggestions for current database
  const SUGGESTED_QUERIES = SUGGESTED_QUERIES_BY_DB[dbId] || [];
  const PREDEFINED_GOALS = PREDEFINED_GOALS_BY_DB[dbId] || [];
  const [localInput, setLocalInput] = useState('');
  const [localGoalInput, setLocalGoalInput] = useState('');
  const textareaRef = useRef(null);

  // Only show mode switcher after first message
  const hasMessages = messages && messages.length > 0;

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_CHARS) {
      setLocalInput(value);
      actions.setCurrentInput(value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    const message = currentInput.trim() || localInput.trim();
    if (message && !isProcessing) {
      actions.sendMessage(message);
      setLocalInput('');
      if (textareaRef.current) {
        textareaRef.current.style.height = '110px';
      }
    }
  };

  const handleInterrupt = () => {
    actions.interruptExecution();
  };

  const handleSuggestionClick = (query) => {
    if (!isProcessing) {
      setLocalInput(query);
      actions.setCurrentInput(query);
      actions.sendMessage(query);
    }
  };

  // Mode switching handlers
  const handleModeSwitch = (mode) => {
    actions.setChatMode(mode);
    if (mode === 'normal') {
      setLocalGoalInput('');
    }
  };

  // Goal submission handler
  const handleGoalSubmit = () => {
    if (localGoalInput.trim() && !isLoadingGoalSuggestions) {
      actions.setGoalText(localGoalInput);
      actions.fetchGoalSuggestions(localGoalInput);
    }
  };

  // Predefined goal click handler
  const handlePredefinedGoalClick = (goalItem) => {
    setLocalGoalInput(goalItem.goal);
    actions.setGoalText(goalItem.goal);
    actions.fetchGoalSuggestions(goalItem.goal);
  };

  // Goal suggestion click handler
  const handleGoalSuggestionClick = (suggestion) => {
    actions.selectGoalSuggestion(suggestion);
    // After selecting a suggestion, switch back to normal mode
    actions.setChatMode('normal');
  };

  // Reset goal handler - go back to predefined goals
  const handleResetGoal = () => {
    actions.clearGoalState();
    setLocalGoalInput('');
  };

  // Auto-resize textarea
  const handleTextareaResize = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '110px';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  };

  React.useEffect(() => {
    handleTextareaResize();
  }, [localInput, currentInput]);

  const [isMinimized, setIsMinimized] = useState(false);
  const dragStartY = useRef(null);

  const inputValue = currentInput || localInput;
  const canSend = inputValue.trim().length > 0 && !isProcessing;

  return (
    <InputContainer>
      <DragHandle
        onPointerDown={(e) => {
          dragStartY.current = e.clientY;
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerUp={(e) => {
          if (dragStartY.current === null) return;
          const delta = e.clientY - dragStartY.current;
          dragStartY.current = null;
          if (Math.abs(delta) < 5) {
            setIsMinimized(m => !m);
          } else if (delta < -20) {
            setIsMinimized(true);
          } else if (delta > 20) {
            setIsMinimized(false);
          }
        }}
        title={isMinimized ? 'انقر للتوسيع' : 'انقر للطي'}
      >
        <HandlePill />
      </DragHandle>
      <AnimatePresence initial={false}>
        {!isMinimized && (
          <motion.div
            key="input-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <InputContent>
      {/* Mode Switcher - only show after first message */}
      {hasMessages && (
        <ModeSwitcherContainer>
          <ModeButton
            $active={chatMode === 'normal'}
            onClick={() => handleModeSwitch('normal')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <MessageSquare />
            استعلام مباشر
          </ModeButton>
          <ModeButton
            $active={chatMode === 'goal'}
            onClick={() => handleModeSwitch('goal')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Target />
            وضع الهدف
          </ModeButton>
        </ModeSwitcherContainer>
      )}

      {/* Goal Mode Panel */}
      <AnimatePresence>
        {hasMessages && chatMode === 'goal' && (
          <GoalInputPanel
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            {goalSuggestions.length > 0 ? (
              <>
                {goalExplanation && (
                  <GoalExplanation>
                    <Lightbulb size={16} />
                    {goalExplanation}
                  </GoalExplanation>
                )}
                <GoalSuggestionsPanel>
                  {goalSuggestions.map((suggestion, index) => (
                    <GoalSuggestionCard
                      key={index}
                      onClick={() => handleGoalSuggestionClick(suggestion)}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <SuggestionLabel>
                        <Send size={12} />
                        {suggestion.label}
                      </SuggestionLabel>
                      <SuggestionQuery>{suggestion.query}</SuggestionQuery>
                    </GoalSuggestionCard>
                  ))}
                </GoalSuggestionsPanel>
                <ResetGoalButton
                  onClick={handleResetGoal}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <RotateCcw />
                  اختر هدف آخر
                </ResetGoalButton>
              </>
            ) : (
              <>
                {/* Predefined Goals */}
                {PREDEFINED_GOALS.length > 0 && (
                  <PredefinedGoalsGrid>
                    {PREDEFINED_GOALS.map((goalItem, index) => (
                      <PredefinedGoalCard
                        key={index}
                        onClick={() => handlePredefinedGoalClick(goalItem)}
                        disabled={isLoadingGoalSuggestions}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <GoalCardIcon>{goalItem.icon}</GoalCardIcon>
                        <GoalCardTitle>{goalItem.title}</GoalCardTitle>
                        <GoalCardDescription>{goalItem.description}</GoalCardDescription>
                      </PredefinedGoalCard>
                    ))}
                  </PredefinedGoalsGrid>
                )}

                {/* Or Divider */}
                {PREDEFINED_GOALS.length > 0 && (
                  <OrDivider>
                    <span>أو اكتب هدفك</span>
                  </OrDivider>
                )}

                {/* Custom Goal Input */}
                <GoalInputField
                  value={localGoalInput}
                  onChange={(e) => setLocalGoalInput(e.target.value)}
                  placeholder="صف هدفك... مثال: أريد فهم أنماط الدفع الرقمي مقارنة بالنقدي"
                  disabled={isLoadingGoalSuggestions}
                />
                <GoalSubmitButton
                  onClick={handleGoalSubmit}
                  disabled={!localGoalInput.trim() || isLoadingGoalSuggestions}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isLoadingGoalSuggestions ? (
                    <>
                      <Loader className="animate-spin" />
                      جاري التحليل...
                    </>
                  ) : (
                    <>
                      <Sparkles />
                      اقترح استعلامات
                    </>
                  )}
                </GoalSubmitButton>
              </>
            )}
          </GoalInputPanel>
        )}
      </AnimatePresence>

      {/* Normal Query Input - hide when goal mode has suggestions */}
      {!(hasMessages && chatMode === 'goal' && goalSuggestions.length > 0) && (
        <>
          <InputWrapper>
            <InputFieldContainer>
              <InputField
                ref={textareaRef}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="اكتب سؤالك هنا..."
                disabled={isProcessing}
                rows={1}
              />
              <CharacterCount>
                {inputValue.length}/{MAX_CHARS}
              </CharacterCount>
            </InputFieldContainer>

            <SendButton
              onClick={handleSend}
              disabled={!canSend}
              whileHover={canSend ? { scale: 1.05 } : {}}
              whileTap={canSend ? { scale: 0.95 } : {}}
            >
              {isProcessing ? (
                <div className="spinner" />
              ) : (
                <Send />
              )}
            </SendButton>

            {isProcessing && (
              <InterruptButton
                onClick={handleInterrupt}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
              >
                <X />
              </InterruptButton>
            )}
          </InputWrapper>

          {/* Suggested Queries - only in normal mode */}
          {chatMode === 'normal' && !isProcessing && SUGGESTED_QUERIES.length > 0 && (
            <SuggestedQueries>
              {SUGGESTED_QUERIES.map((query, index) => (
                <SuggestionChip
                  key={index}
                  onClick={() => handleSuggestionClick(query)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {query}
                </SuggestionChip>
              ))}
            </SuggestedQueries>
          )}
        </>
      )}
          </InputContent>
          </motion.div>
        )}
      </AnimatePresence>
    </InputContainer>
  );
}

export default ChatInput; 